/**
 * Runs the plan vectoriser in a Web Worker and returns a ready IntentEngine — and, a moment after,
 * the plan's stationing (what its station labels say along its alignments), or null when it has none.
 */
import { IntentEngine } from '../vision/engine.ts';
import type { PlanStations } from '../vision/stations.ts';
import type { Drawing } from '../vision/drawing.ts';
import type { EarlyCopy } from '../vision/drawing.worker.ts';
import { fromWire, type PlanWire } from '../vision/types.ts';

let worker: Worker | null = null;
let drawer: Worker | null = null;

/** The drawing worker: it makes the drawing from the vision worker's first reading, passed on here. */
function getDrawer(): Worker {
  if (drawer) return drawer;
  drawer = new Worker(new URL('../vision/drawing.worker.ts', import.meta.url), { type: 'module' });
  drawer.onmessage = (e: MessageEvent<{ id: number; drawing: Drawing | null; error?: string; begun?: number; at?: number }>) => {
    const p = pending.get(e.data.id);
    if (!p) return;
    stamp(e.data.id, 'drawBegun', e.data.begun);
    stamp(e.data.id, 'drawn', e.data.at);
    if (e.data.error) console.warn('drawing failed', e.data.error);
    p.drawing(e.data.drawing ?? null);
    p.drawn = true;
    if (p.stationed && p.edged) pending.delete(e.data.id);
  };
  drawer.onerror = () => {
    for (const p of pending.values()) p.drawing(null);
    drawer?.terminate();
    drawer = null;
  };
  return drawer;
}
let nextId = 1;

/** When each step of each analysis happened (ms, the page's clock), in development: read by the timing scripts. */
const stamps = new Map<number, Record<string, number>>();
function stamp(id: number, what: string, at = performance.timeOrigin + performance.now()): void {
  if (!import.meta.env.DEV) return;
  let s = stamps.get(id);
  if (!s) stamps.set(id, (s = {}));
  s[what] = Math.round(at - performance.timeOrigin);
  (globalThis as { __visionStamps?: Record<string, number> }).__visionStamps = s;
}
interface Job {
  engine: (e: IntentEngine) => void;
  stations: (s: PlanStations | null) => void;
  drawing: (d: Drawing | null) => void;
  reject: (e: Error) => void;
  got: boolean;
  /** The engine made from the plan (its distances arrive after it). */
  made?: IntentEngine;
  stationed?: boolean;
  drawn?: boolean;
  edged?: boolean;
}
const pending = new Map<number, Job>();

function getWorker(): Worker {
  if (worker) return worker;
  worker = new Worker(new URL('../vision/worker.ts', import.meta.url), { type: 'module' });
  worker.onmessage = (e: MessageEvent<{ id: number; wire?: PlanWire; stations?: PlanStations | null; early?: EarlyCopy; edt?: Uint8Array; error?: string; begun?: boolean; at?: number }>) => {
    const p = pending.get(e.data.id);
    if (!p) return;
    if (e.data.begun) {
      stamp(e.data.id, 'begun', e.data.at);
      return;
    }
    stamp(e.data.id, e.data.early ? 'early' : e.data.wire ? 'wire' : e.data.edt ? 'edt' : 'stations' in e.data ? 'stations' : 'error', e.data.at);
    if (e.data.early) {
      const c = e.data.early;
      try {
        getDrawer().postMessage({ id: e.data.id, early: c }, [c.lum.buffer, c.paper.buffer, c.grain.buffer, c.labels.buffer, c.lineMask.buffer, ...(c.luma ? [c.luma.buffer] : [])] as Transferable[]);
      } catch {
        p.drawing(null);
        p.drawn = true;
      }
      return;
    }
    if (e.data.wire) {
      p.got = true;
      p.made = new IntentEngine(fromWire(e.data.wire));
      p.engine(p.made);
      return;
    }
    if (e.data.edt) {
      const plan = p.made?.plan;
      if (plan && plan.edt.length !== plan.cw * plan.ch) plan.edt = e.data.edt;
      p.edged = true;
      if (p.stationed && p.drawn) pending.delete(e.data.id);
      return;
    }
    if ('stations' in e.data) {
      if (e.data.error) console.warn('stationing failed', e.data.error);
      p.stations(e.data.stations ?? null);
      p.stationed = true;
      return;
    }
    pending.delete(e.data.id);
    p.reject(new Error(e.data.error ?? 'vectorise failed'));
    p.stations(null);
    p.drawing(null);
  };
  worker.onerror = (e) => {
    for (const p of pending.values()) {
      if (!p.got) p.reject(new Error(e.message || 'vision worker error'));
      p.stations(null);
      p.drawing(null);
    }
    pending.clear();
    worker?.terminate();
    worker = null;
  };
  return worker;
}

/**
 * The plan analysed in the worker: from its file (decoded there) or a bitmap (transferred to the
 * worker: it becomes unusable here). `withStations`: read the plan's stationing too (a concrete map's).
 */
export function analysePlan(image: ImageBitmap | Blob, withStations = true): { engine: Promise<IntentEngine>; stations: Promise<PlanStations | null>; drawing: Promise<Drawing | null> } {
  const id = nextId++;
  let onEngine!: (e: IntentEngine) => void, onFail!: (e: Error) => void, onStations!: (s: PlanStations | null) => void, onDrawing!: (d: Drawing | null) => void;
  const engine = new Promise<IntentEngine>((res, rej) => {
    onEngine = res;
    onFail = rej;
  });
  const stations = new Promise<PlanStations | null>((res) => (onStations = res));
  const drawing = new Promise<Drawing | null>((res) => (onDrawing = res));
  const job: Job = { engine: onEngine, stations: onStations, drawing: onDrawing, reject: onFail, got: false };
  pending.set(id, job);
  stamp(id, 'asked');
  try {
    if (image instanceof Blob) getWorker().postMessage({ id, blob: image, stations: withStations });
    else getWorker().postMessage({ id, bitmap: image, stations: withStations }, [image]);
  } catch (e) {
    pending.delete(id);
    job.reject(e instanceof Error ? e : new Error(String(e)));
    job.stations(null);
    job.drawing(null);
  }
  return { engine, stations, drawing };
}
